import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Input } from "@vimos/ui";
import { getSupportedColumns } from "../../lib/schema";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../lib/auth";
import { notifyCmsChanged, type CmsTable } from "../../lib/cms-webhook";
import { MediaPicker } from "../media/MediaPicker";
import { getContentConfig, type FieldConfig } from "./contentConfig";

type Row = Record<string, unknown> & { id: string };
type FormState = Record<string, string | boolean | string[]>;

interface RelationOption {
  label: string;
  value: string;
}

type RelationOptions = Partial<Record<NonNullable<FieldConfig["relation"]>, RelationOption[]>>;

function serializeField(field: FieldConfig, value: unknown): string | boolean {
  if (field.type === "checkbox") return Boolean(value);
  if (field.type === "list") return Array.isArray(value) ? value.join("\n") : "";
  if (field.type === "json") return value ? JSON.stringify(value, null, 2) : "";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return "";
}

function defaultValue(field: FieldConfig): string | boolean {
  if (field.type === "checkbox") return false;
  if (field.name === "offer_type") return "saas";
  if (field.name === "billing_period") return "monthly";
  if (field.type === "json") return field.name === "gallery" ? "[]" : "{}";
  return "";
}

function createInitialState(fields: FieldConfig[], row?: Row): FormState {
  return Object.fromEntries(
    fields.map((field) => [field.name, row ? serializeField(field, row[field.name]) : defaultValue(field)]),
  );
}

function parseJsonField(field: FieldConfig, value: string): unknown {
  if (!value.trim()) return field.name === "gallery" ? [] : {};
  try {
    return JSON.parse(value) as unknown;
  } catch {
    throw new Error(`El campo "${field.label}" debe contener JSON valido.`);
  }
}

function validateSlug(field: FieldConfig, value: string) {
  if (field.name === "slug" && value && !/^[a-z0-9-]+$/.test(value)) {
    throw new Error(`El campo "${field.label}" solo acepta minusculas, numeros y guiones.`);
  }
}

function validateUrl(field: FieldConfig, value: string) {
  if (field.type !== "url" && field.type !== "image") return;
  if (!value) return;
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol)) throw new Error("Invalid protocol");
  } catch {
    throw new Error(`El campo "${field.label}" debe ser una URL http o https valida.`);
  }
}

function buildPayload(fields: FieldConfig[], values: FormState, table: string): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const field of fields) {
    const rawValue = values[field.name];

    if (field.type === "checkbox") {
      payload[field.name] = Boolean(rawValue);
      continue;
    }

    const stringValue = String(rawValue ?? "").trim();

    if (field.required && !stringValue) {
      throw new Error(`El campo "${field.label}" es obligatorio.`);
    }

    validateSlug(field, stringValue);
    validateUrl(field, stringValue);

    if (field.type === "number") {
      const numberValue = stringValue ? Number(stringValue) : null;
      if (numberValue !== null && !Number.isFinite(numberValue)) {
        throw new Error(`El campo "${field.label}" debe ser un numero valido.`);
      }
      payload[field.name] = numberValue;
    } else if (field.type === "list") {
      payload[field.name] = stringValue
        ? stringValue
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean)
        : [];
    } else if (field.type === "json") {
      payload[field.name] = parseJsonField(field, stringValue);
    } else {
      payload[field.name] = stringValue || null;
    }
  }

  if (payload.is_published && (table === "pages" || table === "case_studies")) {
    payload.published_at = new Date().toISOString();
  }

  return payload;
}

async function fetchRow(table: string, id: string): Promise<Row> {
  const { data, error } = await supabase
    .from(table as never)
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw error;
  return data as Row;
}

async function fetchRelationOptions(
  relations: NonNullable<FieldConfig["relation"]>[],
): Promise<RelationOptions> {
  const uniqueRelations = Array.from(new Set(relations));
  const options: RelationOptions = {};

  await Promise.all(
    uniqueRelations.map(async (relation) => {
      if (relation === "pages") {
        const { data, error } = await supabase
          .from("pages")
          .select("slug, title")
          .order("title", { ascending: true });
        if (error) throw error;
        const rows = (data ?? []) as Array<{ slug: string; title: string }>;
        options.pages = rows.map((page) => ({
          label: `${page.title} (${page.slug})`,
          value: page.slug,
        }));
      }

      if (relation === "portfolio_items") {
        const { data, error } = await supabase
          .from("portfolio_items")
          .select("id, title, slug")
          .order("title", { ascending: true });
        if (error) throw error;
        const rows = (data ?? []) as Array<{ id: string; slug: string; title: string }>;
        options.portfolio_items = rows.map((item) => ({
          label: `${item.title} (${item.slug})`,
          value: item.id,
        }));
      }
    }),
  );

  return options;
}

function FieldControl({
  field,
  value,
  relationOptions,
  onChange,
  table,
}: {
  field: FieldConfig;
  value: string | boolean | string[];
  relationOptions?: RelationOption[] | undefined;
  onChange: (value: string | boolean | string[]) => void;
  table: string | undefined;
}) {
  if (field.type === "checkbox") {
    return (
      <label className="border-sand/60 bg-paper text-ink flex items-center gap-2 rounded-md border px-3 py-3 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        {field.label}
      </label>
    );
  }

  if (field.type === "image") {
    return (
      <MediaPicker
        label={field.label}
        value={typeof value === "string" ? value : ""}
        onChange={(val) => {
          if (typeof val === "string") onChange(val);
        }}
      />
    );
  }

  if (
    field.type === "json" &&
    table &&
    field.name === "gallery" &&
    (table === "portfolio_items" || table === "case_studies")
  ) {
    const urls = Array.isArray(value) ? (value as string[]) : value ? JSON.parse(String(value)) : [];
    return (
      <div className="flex flex-col gap-2">
        <label className="font-body text-ink text-sm font-medium">{field.label}</label>
        <MediaPicker
          label=""
          value={urls}
          onChange={(next) => onChange(JSON.stringify(next))}
          multiple
          maxItems={12}
        />
        <p className="text-mist text-xs">
          Selecciona hasta 12 imágenes. Usa el selector visual para agregar o quitar fotos.
        </p>
      </div>
    );
  }

  if (field.type === "select" || field.type === "relation") {
    const options =
      field.type === "relation"
        ? [{ label: "Sin relacion", value: "" }, ...(relationOptions ?? [])]
        : (field.options ?? []);

    return (
      <div className="flex flex-col gap-1.5">
        <label className="font-body text-ink text-sm font-medium">{field.label}</label>
        <select
          className="border-sand/70 bg-paper font-body text-ink focus-visible:ring-gold h-11 rounded-md border px-3.5 text-base focus-visible:outline-none focus-visible:ring-2"
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {field.hint ? <p className="text-mist text-sm">{field.hint}</p> : null}
      </div>
    );
  }

  if (field.type === "textarea" || field.type === "list" || field.type === "json") {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="font-body text-ink text-sm font-medium">{field.label}</label>
        <textarea
          rows={field.type === "json" ? 8 : 4}
          className="border-sand/70 bg-paper font-body text-ink focus-visible:ring-gold rounded-md border px-3.5 py-2 text-base focus-visible:outline-none focus-visible:ring-2"
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
        />
        {field.hint ? <p className="text-mist text-sm">{field.hint}</p> : null}
      </div>
    );
  }

  return (
    <Input
      label={field.label}
      type={
        field.type === "number"
          ? "number"
          : field.type === "date"
            ? "date"
            : field.type === "url"
              ? "url"
              : "text"
      }
      required={field.required}
      hint={field.hint}
      value={String(value ?? "")}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function fieldBucket(field: FieldConfig): "principal" | "contenido" | "seo" | "publicacion" {
  if (
    field.name.startsWith("seo_") ||
    field.name.includes("canonical") ||
    field.name.includes("og_") ||
    field.name === "metadata"
  ) {
    return "seo";
  }
  if (
    field.type === "checkbox" ||
    field.name.includes("published") ||
    field.name.includes("featured") ||
    field.name === "sort_order" ||
    field.name === "page_date"
  ) {
    return "publicacion";
  }
  if (
    field.type === "textarea" ||
    field.type === "json" ||
    field.type === "list" ||
    field.type === "image" ||
    field.type === "url" ||
    field.type === "relation" ||
    field.name.includes("image") ||
    field.name.includes("gallery") ||
    field.name.includes("cta")
  ) {
    return "contenido";
  }
  return "principal";
}

function SectionCard({
  title,
  description,
  fields,
  formValues,
  relationOptions,
  onChange,
  table,
}: {
  title: string;
  description: string;
  fields: FieldConfig[];
  formValues: FormState;
  relationOptions: RelationOptions;
  onChange: (field: FieldConfig, value: string | boolean | string[]) => void;
  table: string | undefined;
}) {
  if (fields.length === 0) return null;

  return (
    <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
      <div className="border-sand/40 border-b pb-4">
        <h2 className="font-display text-ink text-xl font-bold">{title}</h2>
        <p className="text-mist mt-1 text-sm">{description}</p>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {fields.map((field) => (
          <div
            key={field.name}
            className={
              field.type === "textarea" ||
              field.type === "json" ||
              field.type === "list" ||
              field.type === "image"
                ? "sm:col-span-2"
                : ""
            }
          >
            <FieldControl
              field={field}
              value={formValues[field.name] ?? defaultValue(field)}
              relationOptions={field.relation ? relationOptions[field.relation] : undefined}
              onChange={(value) => onChange(field, value)}
              table={table}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function PreviewPanel({ values, section }: { values: FormState; section: string }) {
  const title = String(values.title ?? values.name ?? values.question ?? values.storage_path ?? "Sin titulo");
  const summary = String(
    values.summary ?? values.excerpt ?? values.subtitle ?? values.answer ?? values.alt_text ?? "",
  );
  const slug = String(values.slug ?? "");
  const imageUrl = String(
    values.cover_image_url ?? values.hero_image_url ?? values.image_url ?? values.public_url ?? "",
  );
  const isPublished = Boolean(values.is_published);
  const price = values.price_amount ? `S/${values.price_amount}` : null;

  return (
    <aside className="sticky top-6 space-y-4">
      <section className="border-sand/40 bg-paper shadow-card overflow-hidden rounded-md border">
        <div className="aspect-[16/9] bg-[linear-gradient(135deg,rgba(11,19,43,0.95),rgba(201,162,77,0.42))]">
          {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between gap-3">
            <Badge tone={isPublished ? "market" : "neutral"}>{isPublished ? "Publicado" : "Borrador"}</Badge>
            <span className="text-mist text-xs uppercase">{section}</span>
          </div>
          <h2 className="font-display text-ink mt-4 text-2xl font-bold">{title}</h2>
          {price ? <p className="font-display text-signal-hover mt-2 text-xl font-bold">{price}</p> : null}
          {summary ? <p className="text-mist mt-3 line-clamp-4 text-sm leading-relaxed">{summary}</p> : null}
          {slug ? (
            <p className="text-mist mt-4 rounded-md bg-[#f7f7f4] px-3 py-2 font-mono text-xs">/{slug}</p>
          ) : null}
        </div>
      </section>
      <section className="border-sand/40 bg-paper shadow-card rounded-md border p-5">
        <h3 className="font-display text-ink text-lg font-bold">Checklist rapido</h3>
        <ul className="text-mist mt-3 space-y-2 text-sm">
          <li>{title !== "Sin titulo" ? "OK" : "Pendiente"} - titulo o nombre</li>
          <li>
            {slug || section === "faqs" || section === "media" ? "OK" : "Pendiente"} - slug o identificador
          </li>
          <li>{summary ? "OK" : "Pendiente"} - resumen o descripcion</li>
          <li>{imageUrl ? "OK" : "Opcional"} - imagen principal</li>
        </ul>
      </section>
    </aside>
  );
}

export function ContentEditorPage({ section }: { section: string }) {
  const { id: recordId } = useParams<{ id: string }>();
  const isNew = recordId === "nuevo";
  const postId = isNew ? null : recordId;
  const config = getContentConfig(section);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const [formValues, setFormValues] = useState<FormState>(() => createInitialState(config.fields));
  const [formError, setFormError] = useState<string | null>(null);

  const hasValidId = isNew || Boolean(postId);

  const { data: supportedColumns } = useQuery({
    queryKey: ["schema", config.table],
    queryFn: () => getSupportedColumns(config.table),
  });

  const activeFields = useMemo(
    () =>
      supportedColumns ? config.fields.filter((field) => supportedColumns.has(field.name)) : config.fields,
    [config.fields, supportedColumns],
  );

  const hiddenFields = useMemo(
    () => (supportedColumns ? config.fields.filter((field) => !supportedColumns.has(field.name)) : []),
    [config.fields, supportedColumns],
  );

  const relations = useMemo(
    () =>
      activeFields
        .map((field) => field.relation)
        .filter((relation): relation is NonNullable<FieldConfig["relation"]> => Boolean(relation)),
    [activeFields],
  );

  const { data: relationOptions = {} } = useQuery({
    queryKey: ["relations", config.table, relations.join(",")],
    queryFn: () => fetchRelationOptions(relations),
    enabled: relations.length > 0,
  });

  const { data: row, isLoading } = useQuery({
    queryKey: ["content", config.table, postId],
    queryFn: () => fetchRow(config.table, postId!),
    enabled: !isNew && Boolean(postId),
  });

  useEffect(() => {
    setFormValues(createInitialState(activeFields, row));
  }, [activeFields, row]);

  const title = useMemo(
    () => (isNew ? `Crear ${config.title}` : `Editar ${config.title}`),
    [config.title, isNew],
  );

  const saveMutation = useMutation<string, Error, Partial<FormState>>({
    mutationFn: async (override?: Partial<FormState>) => {
      setFormError(null);
      const nextValues = { ...formValues, ...(override ?? {}) } as FormState;
      const payload = buildPayload(activeFields, nextValues, config.table);

      if (isNew) {
        const { data, error } = await supabase
          .from(config.table)
          .insert(payload as never)
          .select("id")
          .single();
        if (error) throw error;
        const insertedId = (data as { id: string } | null)?.id;
        if (!insertedId) throw new Error("No se pudo obtener el ID del registro creado.");
        return insertedId;
      } else {
        if (!postId) throw new Error("ID de registro requerido.");
        const { error } = await supabase
          .from(config.table)
          .update(payload as never)
          .eq("id", postId);
        if (error) throw error;
        return postId;
      }
    },
    onSuccess: async (savedId: string) => {
      queryClient.invalidateQueries({ queryKey: ["content", config.table] });
      queryClient.invalidateQueries({ queryKey: ["content", config.table, postId] });

      try {
        await notifyCmsChanged(
          config.table as CmsTable,
          isNew ? "insert" : "update",
          savedId,
          session?.access_token,
        );
      } catch (webhookError) {
        console.error("Error al notificar cambio CMS", webhookError);
      }

      navigate(`/${section}`);
    },
    onError: (error) => {
      setFormError(error instanceof Error ? error.message : "No pudimos guardar este registro.");
    },
  });

  const fieldsByBucket = useMemo(
    () => ({
      principal: activeFields.filter((field) => fieldBucket(field) === "principal"),
      contenido: activeFields.filter((field) => fieldBucket(field) === "contenido"),
      seo: activeFields.filter((field) => fieldBucket(field) === "seo"),
      publicacion: activeFields.filter((field) => fieldBucket(field) === "publicacion"),
    }),
    [activeFields],
  );

  function updateField(field: FieldConfig, value: string | boolean | string[]) {
    setFormValues((current) => ({ ...current, [field.name]: value }));
  }

  return (
    <div className="max-w-7xl">
      {!hasValidId ? (
        <p className="mt-6 text-red-600">ID de registro invalido.</p>
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="font-display text-ink text-2xl font-bold">{title}</h1>
              <p className="text-mist mt-1 text-sm">{config.description}</p>
            </div>
            <Link to={`/${section}`}>
              <Button type="button" variant="ghost">
                Volver
              </Button>
            </Link>
          </div>

          {hiddenFields.length > 0 ? (
            <p className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Migracion pendiente en Supabase: {hiddenFields.map((field) => field.name).join(", ")}. Estos
              campos no se muestran ni se guardan para evitar errores HTTP 400.
            </p>
          ) : null}

          {isLoading ? <p className="text-mist mt-6">Cargando...</p> : null}

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <form
              className="space-y-5"
              onSubmit={(event) => {
                event.preventDefault();
                saveMutation.mutate({});
              }}
            >
              <SectionCard
                title="Informacion principal"
                description="Datos que identifican el registro y se usan para listados, URLs y busqueda."
                fields={fieldsByBucket.principal}
                formValues={formValues}
                relationOptions={relationOptions}
                onChange={updateField}
                table={config.table}
              />
              <SectionCard
                title="Contenido y experiencia"
                description="Texto comercial, elementos visuales, listas, galerias y CTA."
                fields={fieldsByBucket.contenido}
                formValues={formValues}
                relationOptions={relationOptions}
                onChange={updateField}
                table={config.table}
              />
              <SectionCard
                title="SEO y metadata"
                description="Metadata para buscadores, enlaces sociales y migracion ordenada."
                fields={fieldsByBucket.seo}
                formValues={formValues}
                relationOptions={relationOptions}
                onChange={updateField}
                table={config.table}
              />
              <SectionCard
                title="Publicacion"
                description="Estado, destacado y orden editorial."
                fields={fieldsByBucket.publicacion}
                formValues={formValues}
                relationOptions={relationOptions}
                onChange={updateField}
                table={config.table}
              />

              {formError ? (
                <p
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {formError}
                </p>
              ) : null}

              <div className="border-sand/40 sticky bottom-0 flex flex-wrap gap-3 border-t bg-[#f7f7f4]/95 py-4 backdrop-blur">
                <Button type="submit" isLoading={saveMutation.isPending}>
                  Guardar cambios
                </Button>
                {config.hasPublished && activeFields.some((field) => field.name === "is_published") ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => saveMutation.mutate({ is_published: false })}
                    >
                      Guardar borrador
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => saveMutation.mutate({ is_published: true })}
                    >
                      Publicar
                    </Button>
                  </>
                ) : null}
                <Button type="button" variant="ghost" onClick={() => navigate(`/${section}`)}>
                  Cancelar
                </Button>
              </div>
            </form>

            <PreviewPanel values={formValues} section={section} />
          </div>
        </>
      )}
    </div>
  );
}
